/**
 * Because of a known restriction on Final Form, numeric field names
 * will throw an error (even if they're strings!). Therefore we concatenate all field names
 * with a known string when rendering, but this causes the form
 * output to have incorrect field names, so we need to do the
 * reverse transformation before submitting the form!
 *
 * https://github.com/final-form/react-final-form/blob/master/docs/faq.md#why-cant-i-have-numeric-keys-in-an-object
 */
import { __awaiter } from "tslib";
import { useCallback, useMemo } from 'react';
import { FORM_ERROR } from 'final-form';
import { assert } from '../../utils';
const FIELD_NAME_PREFIX = 'field~';
const DUMMY_FIELD_NAME_PREFIX = 'dummyField~';
export function encodeFieldName(fieldName, isDummy = false) {
    const prefix = isDummy ? DUMMY_FIELD_NAME_PREFIX : FIELD_NAME_PREFIX;
    return fieldName.startsWith(prefix) ? fieldName : `${prefix}${fieldName}`;
}
// Loop through all object keys at root level and encode the field name
// Output is an object with corrected keys
export function encodeFieldNames(formValue) {
    if (formValue) {
        const entries = Object.entries(formValue).map(([fieldName, fieldValue]) => [
            encodeFieldName(fieldName),
            fieldValue,
        ]);
        return Object.fromEntries(entries);
    }
}
export function decodeFieldName(encodedFieldName) {
    assert(encodedFieldName.startsWith(DUMMY_FIELD_NAME_PREFIX) || encodedFieldName.startsWith(FIELD_NAME_PREFIX), 'Field name was not correctly set up');
    // if dummy we don't want to return anything for submission
    return encodedFieldName.startsWith(DUMMY_FIELD_NAME_PREFIX) ? undefined : encodedFieldName.split('~')[1];
}
// Loop through all object keys at root level and decode the field name
// Output is an object with corrected keys
export function decodeFieldNames(formValue) {
    const entries = Object.entries(formValue).map(([fieldName, fieldValue]) => [decodeFieldName(fieldName), fieldValue]);
    return Object.fromEntries(entries);
}
function mapObjectValues(object, getValue) {
    const entries = Object.entries(object).map(([fieldName, fieldValue]) => [fieldName, getValue(fieldName, fieldValue)]);
    return Object.fromEntries(entries);
}
function transformValuesForForm(valueTransformer, initialValues) {
    return valueTransformer ? mapObjectValues(initialValues, valueTransformer.toFormValue) : initialValues;
}
function transformValuesForSubmission(valueTransformer, formValues) {
    return __awaiter(this, void 0, void 0, function* () {
        if (valueTransformer) {
            const entryPromises = Object.entries(formValues).map(([fieldName, fieldValue]) => {
                // this may return a promise, so after we get it we wrap it
                // in `Promise.resolve()` to guarantee async behaviour
                const valueToSubmit = valueTransformer.toSubmittedValue(fieldName, fieldValue);
                // We avoid `await` to make it clear that this fn returns
                // a promise.
                //
                // eslint-disable-next-line promise/prefer-await-to-then
                return Promise.resolve(valueToSubmit).then((resolvedValue) => [fieldName, resolvedValue]);
            });
            const entries = yield Promise.all(entryPromises);
            return Object.fromEntries(entries);
        }
        return formValues;
    });
}
export function useHandleSubmitWithDecoding(onSubmit, valueTransformer, initialValues) {
    return useCallback((formData) => __awaiter(this, void 0, void 0, function* () {
        const decodedFormValues = decodeFieldNames(formData);
        const transformedFormValues = yield transformValuesForSubmission(valueTransformer, decodedFormValues);
        try {
            return yield onSubmit(transformedFormValues, initialValues);
        }
        catch (submitError) {
            console.error(submitError);
            return {
                [FORM_ERROR]: submitError.message,
            };
        }
    }), [onSubmit, valueTransformer, initialValues]);
}
export function useInitialValuesWithEncoding(initialValues, valueTransformer) {
    return useMemo(() => {
        const transformedValues = transformValuesForForm(valueTransformer, initialValues);
        return encodeFieldNames(transformedValues);
    }, [initialValues, valueTransformer]);
}
//# sourceMappingURL=transformFieldNames.js.map